import styled, { css } from 'styled-components';
import {
  Autocomplete,
  Box,
  TextField,
  InputAdornment,
  Select,
} from '@mui/material';

import colors from '../../../../styles/colors';
import ProductSelector from '../ProductSelector';
import PriceSelector from '../PriceSelector';
import { Section } from '../Section';
import AveragePayment from '../AveragePayment';
import { InfoBox } from '../ContractEditor/styles';
import Input from '../Input';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 500px;
`;

const SidePanel = styled(Box)`
  background-color: ${colors.base.gamma};
`;

const sidePanelStyle = {
  borderRight: `1px solid ${colors.shades.gamma1}`,
  minWidth: '350px',
  boxSizing: 'border-box',
};

const Toolbar = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  height: 10%;
  padding: 15px 40px 0;
  background-color: ${colors.shades.secondary10};
`;

const TextFieldLabel = styled.div`
  color: ${colors.shades.gamma10};
  text-transform: uppercase;
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 19px;
`;

const FormField = styled.div`
  display: ${({ $hidden }) => ($hidden ? 'none' : 'flex')};
  flex-direction: column;
  justify-content: center;
`;

// The challenge's program pickers (one, or a gym and a home one) stacked in one column, with the link that swaps them.
const ProgramFields = styled(FormField)`
  gap: 10px;
`;

// Says why a started challenge's program pickers are locked.
const ProgramsFrozenNote = styled(InfoBox)`
  margin: 0 0 16px;
`;

const ProgramCoverageError = styled.div`
  margin-top: 6px;
  /* Match the program selector's width so the message wraps within it instead of widening the column. */
  width: 265px;
  max-width: 100%;
  color: ${colors.shades.danger3};
  font-size: 12px;
  line-height: 16px;
`;

const StyledInput = styled(Input)`
  .MuiOutlinedInput-input {
    height: 29px;
    width: 32px;
  }

  /*
    Room for three digits. A week term runs to 52 where a month one stops at 12,
    and nothing stops a coach typing past either — a clipped number reads as a
    rendering bug rather than as the out-of-range value it is. Applies to the
    commitment too, which mirrors the term on challenge contracts.
  */
  ${({ $wideNumber }) => $wideNumber && css`
    .MuiOutlinedInput-input {
      width: 42px;
    }
  `}

  /*
    With a unit picker in the adornment slot the input is a two-control row, so
    the field has to span the widened column instead of hugging the number.
  */
  ${({ $hasUnitPicker }) => $hasUnitPicker && css`
    width: 100%;

    .MuiInputBase-adornedEnd {
      padding-right: 8px;
    }
  `}
`;

const StyledTextField = styled(TextField)`
  background-color: ${colors.base.alpha};

  .MuiOutlinedInput-input {
    height: 40px;
    background-color: ${colors.base.alpha};
  }

  .MuiInputBase-adornedEnd {
    text-transform: uppercase;
  }

  .MuiInputAdornment-root {
    margin-left: 0;
    min-width: 68px;
  }

  .MuiFormHelperText-contained {
    position: absolute;
    top: 100%;
    margin-left: 0;
    width: max-content;
  }
`;

const StyledAdornment = styled(InputAdornment)`
  background-color: ${colors.base.alpha};
`;

/*
  Sits where the static "month(s)" adornment sits on non-challenge contracts, so
  the term row keeps its layout whether the unit is fixed or selectable. Uses the
  MUI Select directly rather than the shared components/Select, whose 265px
  min-width and inner border are sized for a standalone form field.

  The outlined input is a flex row, so the select needs flex-shrink: 0 — without
  it the number input's fixed width squeezes the select below its own width and
  the longest option ellipsizes to "WE…". Width fits "MONTHS" (uppercased by the
  Input's adornedEnd rule) plus the dropdown arrow.
*/
const StyledTermUnitSelect = styled(Select)`
  background-color: ${colors.base.alpha};
  /* Sized to its own label so the arrow sits right after the word rather than
     at the far edge of a fixed-width box. */
  width: auto;
  flex-shrink: 0;
  height: 29px;

  .MuiSelect-select {
    /*
      The padding is the arrow's strip, and it belongs to the select div so the
      whole control is clickable — MUI marks the arrow itself
      pointer-events:none, so anything this div doesn't cover is dead to a
      click. Flex-centered to sit on the same baseline as the number beside it.
    */
    display: flex;
    align-items: center;
    box-sizing: border-box;
    width: auto;
    height: 100%;
    padding: 0 22px 0 0;
    min-height: unset;
    font-size: 14px;
    text-overflow: clip;
  }

  .MuiOutlinedInput-notchedOutline {
    border: none;
  }

  .MuiSelect-icon {
    right: 0;
  }
`;

const StyledAutoComplete = styled(Autocomplete)`
  .MuiAutocomplete-inputRoot {
    height: 56px;
  }
`;

const StyledProductSelector = styled(ProductSelector)`
  flex-basis: 50%;
  min-width: 350px;
`;

const ChallengeBadge = styled.span`
  background-color: ${colors.shades.primary3};
  color: ${colors.shades.primary6};
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 4px;
  margin-left: 10px;
`;

const ConfigRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

/*
  The challenge's fields line up at the top: its program column grows with the link under it, and with a second
  picker. The deadline's date picker carries its label inside its outline, so it drops by the height of the label
  the selects show above theirs (11px type on a 12px line, 5px under it) to line its box up with theirs.
*/
const ChallengeConfigRow = styled(ConfigRow)`
  align-items: flex-start;
`;

const AcceptUntilField = styled(FormField)`
  padding-top: 17px;
`;

const RowItem = styled.div`
  width: 130px;
  height: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const CheckoutPaymentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const InitialPaymentContainer = styled(RowItem)`
  width: 400px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-right: 50px;

  /* The unit picker is wider than the static "MONTH(S)" label it replaces. */
  ${({ $hasUnitPicker }) => $hasUnitPicker && css`
    width: 470px;
  `}
`;

/*
  The term column is sized for a static "MONTH(S)" label. A challenge contract
  swaps that label for a unit picker, so the column widens to fit the select plus
  the number input beside it.
*/
const TermRowItem = styled(RowItem)`
  ${({ $hasUnitPicker }) => $hasUnitPicker && css`
    width: 200px;
  `}
`;

const InitialPaymentRowItem = styled(RowItem)`
  width: 150px;
  min-width: 150px;
`;

const InitialTermText = styled(RowItem)`
  width: 40px;
  height: 76px;
  margin-top: 18px;
`;

const CommitmentContainer = styled(RowItem)`
  margin-left: 50px;
  padding-bottom: 27px;
`;

const CancellationDate = styled(RowItem)`
  width: 235px;
  height: 76px;
  margin: 18px 0 27px 18px;
  font-style: italic;
  font-size: 14px;

  #date {
    font-weight: 600;
  }
`;

const PostCommitmentStartDate = styled(CancellationDate)`
  width: 145px;
  margin: 14px 0 0;
`;

const PostCommitmentContainer = styled(RowItem)`
  flex-direction: row;
  width: fit-content;
  gap: 30px;
`;

const MonthlyPriceContainer = styled(RowItem)`
  display: flex;
  flex-direction: row;
  width: fit-content;
  gap: 55px;
`;

const UpsellContainer = styled(RowItem)`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  height: auto;
  width: 100%;
  gap: 30px;
`;

const UpsellSection = styled(Section)`
  display: flex;
  flex-direction: column;
`;

const UpsellNote = styled.div`
  font-size: 13px;
  color: ${colors.shades.gamma7};
  margin-top: 8px;

  a {
    margin-left: 4px;
  }
`;

const StartDateContainer = styled(RowItem)`
  width: 250px;
  height: 58px;
  padding-bottom: 25px;
`;

const ProductPackageContainer = styled(Section)`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  background-color: ${colors.shades.secondary10};
  border-bottom: 1px solid ${colors.shades.secondary7};
`;

const SubSection = styled.div`
  width: 350px;
  margin-right: 35px;
`;

const PackagesContainer = styled.div`
  height: 45px;
`;

const StyledAveragePayment = styled(AveragePayment)`
  margin-top: 12px;
`;

const StyledDatePickerInput = styled(Input)`
  .MuiInputBase-root {
    height: 46px;
  }

  .MuiInputBase-input {
    min-width: 85%;
  }
`;

const TotalFeesContainer = styled.div`
  margin-top: 25px;
  color: ${colors.base.beta};
  font-size: 14px;
  font-weight: 500;
  line-height: 14px;

  > span {
    color: ${colors.shades.gamma10};
  }
`;

const BillingOptionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: 80px;
  padding-bottom: 15px;
  align-items: center;
`;

const BillingOptionsRadioGroup = styled.div`
  min-width: 300px;
`;

const BillingOptionsPriceSelector = styled(PriceSelector)`
  width: 170px;
`;

const StyledPriceSelector = styled(PriceSelector)`
  width: 220px;
`;

const BillingOptionsInfo = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 15px;
`;

const ContractLabelContainer = styled.div`
  display: flex;
  flex-direction: row;
  font-size: 12px;
  line-height: 21px;
`;

const ContractLabel = styled.div`
  color: ${colors.shades.gamma10};
`;

const ContractValue = styled.div`
  color: ${colors.base.beta};
  margin-left: 10px;
`;

const Wrapper = styled.div`
  width: 100%;
`;

/*
 * Wraps instead of holding three columns: on a narrow window three fixed tracks squeeze the cards
 * until their prices and feature lists clip.
 *
 * auto-fill, not auto-fit: auto-fit collapses the empty tracks, so the single card left after a
 * selection would stretch across the whole row instead of keeping a card's width.
 */
const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 14px;
`;

// A text link that acts in the form (changing the package, swapping the challenge's programs).
const LinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  align-self: flex-start;
  background: none;
  border: none;
  padding: 8px 0 0;
  color: ${colors.base.secondary};
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;

  .MuiSvgIcon-root {
    font-size: 16px;
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }

  &:hover:not(:disabled) {
    opacity: 0.8;
  }
`;

export {
  TextFieldLabel,
  FormField,
  ProgramFields,
  ProgramsFrozenNote,
  ProgramCoverageError,
  StyledTextField,
  StyledAutoComplete,
  SidePanel,
  sidePanelStyle,
  StyledProductSelector,
  ConfigRow,
  ChallengeConfigRow,
  AcceptUntilField,
  RowItem,
  InitialPaymentContainer,
  InitialPaymentRowItem,
  InitialTermText,
  CommitmentContainer,
  CancellationDate,
  StyledAdornment,
  StyledTermUnitSelect,
  TermRowItem,
  MonthlyPriceContainer,
  StartDateContainer,
  ProductPackageContainer,
  SubSection,
  CheckoutPaymentWrapper,
  StyledAveragePayment,
  StyledDatePickerInput,
  PackagesContainer,
  Toolbar,
  Container,
  TotalFeesContainer,
  BillingOptionsContainer,
  BillingOptionsRadioGroup,
  BillingOptionsPriceSelector,
  BillingOptionsInfo,
  ContractLabelContainer,
  ContractLabel,
  ContractValue,
  StyledPriceSelector,
  PostCommitmentStartDate,
  PostCommitmentContainer,
  StyledInput,
  ChallengeBadge,
  UpsellContainer,
  UpsellNote,
  UpsellSection,
  Wrapper,
  CardGrid,
  LinkButton,
};
